import { print } from 'graphql';
import Config from '../../config';
import refreshTokenMutation from './refresh-token.graphql';

const STORAGE_KEY = 'daybook_refresh_token';

// A single in-flight refresh so concurrent Unauthenticated errors share one call
let inFlightRefresh: Promise<boolean> | null = null;

// Once a refresh has failed and we've forced a re-login, remember it so the
// auth-error link doesn't keep re-triggering the "me" refetch (which would
// re-enter the link and storm the server) for the rest of the dead-token episode.
let reloginForced = false;

export function saveRefreshToken(token: string): void {
    reloginForced = false;
    try {
        window.localStorage.setItem(STORAGE_KEY, token);
    } catch (e) {
        console.warn('Unable to store refresh token', e);
    }
}

// Returns true exactly once per dead-token episode (reset by saveRefreshToken),
// so callers force the re-login refetch at most once.
export function forceReloginOnce(): boolean {
    if (reloginForced) {
        return false;
    }
    reloginForced = true;
    return true;
}

export function getRefreshToken(): string | null {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return null;
    }
}

export function clearRefreshToken(): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        // ignore
    }
}

// Refreshes the access token using the stored refresh token.
// Returns true on success (a new token cookie is set by the backend response).
// Concurrent calls are deduplicated onto a single in-flight request.
export function refreshAuth(): Promise<boolean> {
    if (inFlightRefresh) {
        return inFlightRefresh;
    }
    inFlightRefresh = doRefresh().finally(() => {
        inFlightRefresh = null;
    });
    return inFlightRefresh;
}

async function doRefresh(): Promise<boolean> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return false;
    }
    try {
        const response = await fetch(Config.graphqlUri, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                query: print(refreshTokenMutation),
                variables: { refresh_token: refreshToken },
            }),
        });
        const json = await response.json();
        const payload = json?.data?.refreshToken;
        if (payload?.access_token && payload?.refresh_token) {
            // Passport rotates the refresh token on every refresh, so persist the new one
            saveRefreshToken(payload.refresh_token);
            return true;
        }
        clearRefreshToken();
        return false;
    } catch (e) {
        console.warn('Token refresh failed', e);
        clearRefreshToken();
        return false;
    }
}
